import {getAwpApiBase} from '../awpApiBase';
import {getAwpClientHeaders} from '../../session/awpRequestHeaders';

/**
 * Admin-only: permanently remove a KB entity (entities row + properties/summaries).
 *
 * @param {string|number} entityRef numeric id, D{n}, or Wikidata Q-id
 * @returns {Promise<{
 *   entityId: number,
 *   canonicalName: string,
 *   wikidataId: string|null,
 *   propertiesDeleted: number,
 *   summariesDeleted: number,
 *   segmentMentionsDeleted: number,
 * }>}
 */
export async function deleteAdminKbEntity(entityRef) {
    const res = await fetch(
        `${getAwpApiBase()}/api/admin/entities/${encodeURIComponent(String(entityRef))}`,
        {
            method: 'DELETE',
            headers: getAwpClientHeaders(),
        },
    );
    if (res.status === 404) {
        const err = new Error('Entity not found');
        err.status = 404;
        throw err;
    }
    if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try {
            const body = await res.json();
            if (body?.message) detail = body.message;
        } catch {
            /* ignore */
        }
        const err = new Error(detail);
        err.status = res.status;
        throw err;
    }
    return res.json();
}

/**
 * Admin-only: paginated list of entity mentions for a report, grouped by link status.
 *
 * @param {string} reportId
 * @param {{
 *   status?: 'all' | 'wikidata' | 'did' | 'unlinked',
 *   q?: string,
 *   page?: number,
 *   size?: number,
 * }} [params]
 * @returns {Promise<{
 *   totalCount: number,
 *   page: number,
 *   size: number,
 *   status: string,
 *   mentions: Array<{
 *     mentionText: string,
 *     entityLabel: string,
 *     wikidataId: string|null,
 *     linkSource: 'auto'|'manual-annotation'|'manual-propagated'|null,
 *     linkedBy: string|null,
 *     nerScore: number|null,
 *     linkingScore: number|null,
 *     blockId: string|null,
 *     contextText: string|null,
 *     mentionStart: number|null,
 *     mentionEnd: number|null,
 *     status: 'wikidata' | 'did' | 'unlinked',
 *   }>
 * }>}
 */
export async function getReportMentions(reportId, { status = 'all', q, page = 0, size = 50 } = {}) {
    const qs = new URLSearchParams({ status, page: String(page), size: String(size) });
    if (q) qs.set('q', q);
    const res = await fetch(
        `${getAwpApiBase()}/api/internal/reports/${encodeURIComponent(reportId)}/mentions?${qs}`,
        { headers: getAwpClientHeaders() },
    );
    if (!res.ok) {
        const err = new Error(`HTTP ${res.status}`);
        err.status = res.status;
        throw err;
    }
    return res.json();
}

/**
 * Admin-only: row counts per status bucket for a report, in a single call.
 * Used to preload tab badges (All Linked / Wikidata / DID / Unlinked) without
 * firing 4 separate paginated requests.
 *
 * @param {string} reportId
 * @param {{q?: string}} [params] optional substring filter, kept in sync with an active search
 * @returns {Promise<{all: number, wikidata: number, did: number, unlinked: number}>}
 */
export async function getReportMentionCounts(reportId, { q } = {}) {
    const qs = new URLSearchParams();
    if (q) qs.set('q', q);
    const suffix = qs.toString() ? `?${qs}` : '';
    const res = await fetch(
        `${getAwpApiBase()}/api/internal/reports/${encodeURIComponent(reportId)}/mentions/counts${suffix}`,
        { headers: getAwpClientHeaders() },
    );
    if (!res.ok) {
        const err = new Error(`HTTP ${res.status}`);
        err.status = res.status;
        throw err;
    }
    return res.json();
}

/**
 * Admin-only: fetch entity statistics for a single report.
 *
 *   totalMentions    – all wikidata_claims rows (yellow marks stored in the document)
 *   wikidataEntities – distinct Wikidata QIDs (Q…) among wikidata_claims
 *   didEntities      – distinct custom DIDs (D…) among wikidata_claims
 *   linkedToWikidata – wikidata_claims rows whose wikidata_id is a Q…
 *   linkedToDid      – wikidata_claims rows whose wikidata_id is a D…
 *   unlinkedMentions – raw NER hits (segment_entities) with no wikidata_claims link, still
 *                      present in the segment's current text (stale-mention guard applied)
 *
 * @param {string} reportId
 * @returns {Promise<{
 *   totalMentions: number,
 *   wikidataEntities: number,
 *   didEntities: number,
 *   linkedToWikidata: number,
 *   linkedToDid: number,
 *   unlinkedMentions: number,
 * }>}
 */
export async function getReportEntityStats(reportId) {
    const res = await fetch(
        `${getAwpApiBase()}/api/admin/entities/report-stats?reportId=${encodeURIComponent(reportId)}`,
        { headers: getAwpClientHeaders() },
    );
    if (!res.ok) {
        const err = new Error(`HTTP ${res.status}`);
        err.status = res.status;
        throw err;
    }
    return res.json();
}
